/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { buildHistogramTimeline, HistogramEndpointResponse } from 'srs-middleware';
import { NonIdealState, ResizeSensor } from '@blueprintjs/core';
import { formatSRSTimeline, TimelineChart } from 'utilities/srs-timeline-library/timeline';
import { Bars } from 'utilities/srs-timeline-library/bar';
import { TimelineUseContext } from 'utilities/srs-timeline-library/timelineUseContext';
import { Keyframe } from 'utilities/srs-timeline-library/keyframe';
import ChartTooltip from 'components/chartTooltip';
import { formatDistance } from 'date-fns';
import { useTooltip } from 'components/chartTooltip/useTooltip';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import { ColorAliases, Colors } from 'styles/variables';
import LoadingIcon from 'components/loadingIcon';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { sendUsageAction } from 'utilities/usageStats';
import { scaleLog, sum } from 'd3';

/**
 * BUG with hover and dragging interaction
 * see comments on https://jira.pnnl.gov/jira/browse/SERBERUS-513 for context
 */
interface Props extends React.ComponentProps<'div'> {
	maxBinResolution?: TimelineBinResolution;
}

/**
 *  Enum to mirror MaxBinResolution values in SRS
 * Note: Only a few values are uncommented right now.
 * 	In the future, we may want to consider a more advanced UI that gives the user more granular control over this.
 * */
export enum TimelineBinResolution {
	Automatic = 'Unspecified',
	// Second = Second,
	// FiveSecond = FiveSecond,
	// FifteenSecond = FifteenSecond,
	// ThirtySecond = ThirtySecond,
	// Minute = Minute,
	// FiveMinutes = FiveMinutes,
	// TenMinutes = TenMinutes,
	// FifteenMinutes = FifteenMinutes,
	// ThirtyMinutes = ThirtyMinutes,
	// Hour = Hour,
	// TwoHour = TwoHour,
	// ThreeHour = ThreeHour,
	// SixHour = 13,
	// TwelveHour = TwelveHour,
	// Day = Day,
	// TwoDay = TwoDay,
	// Week = Week,
	// TwoWeek = TwoWeek,
	Month = 'Month',
	// TwoMonth = TwoMonth,
	Quarter = 'ThreeMonth',
	// SixMonth = SixMonth,
	Year = 'Year',
	// TwoYear = TwoYear,
	// FiveYear = FiveYear,
	// Decade = Decade,
	// Century = Century
}

export const Timeline: React.FC<Props> = ({ maxBinResolution, ...props }) => {
	const [toolTip, setupTrigger] = useTooltip({
		placement: 'bottom-start',
		modifiers: [
			{
				name: 'offset',
				options: {
					offset: [0, 8],
				},
			},
		],
	});

	// Get state from context
	const {
		state: {
			explorer: {
				filters: {
					timeline = {
						timeExtents: null,
						timeProperty: null,
					},
				},
			},
			transient: { activeResult, rootResult },
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const { timeExtents, timeProperty } = timeline;

	// Update the time filter
	const addTimeFilter = useCallback(
		(timeExtents) => {
			dispatch(
				WorkspaceActionCreator.updateFilter('timeline', {
					...timeline,
					timeExtents,
				})
			);
			sendUsageAction('TimelineFilterAdded', () => {
				return `${timeExtents[0].toISOString()} to ${timeExtents[1].toISOString()}`;
			});
		},
		[timeline, dispatch]
	);

	useEffect(() => {
		if (!timeProperty || !timeExtents) return;

		const queryString = {
			$and: [{ [timeProperty.name]: { $gte: timeExtents[0] } }, { [timeProperty.name]: { $lt: timeExtents[1] } }],
		};
		dispatch(WorkspaceActionCreator.updateQueryString('timeline', queryString));
	}, [dispatch, timeExtents, timeProperty]);

	// handle resizing
	const [dimensions, setDimensions] = useState({ height: null, width: null });
	const handleTimelineResize = useCallback(
		(elements) => {
			const dim = elements[0].contentRect;
			setDimensions({
				height: dim.height,
				width: dim.width,
			});
		},
		[setDimensions]
	);

	// NOTE: Comparing results doesn't seem to work well for detecting the presence
	//  of a filter. Using the presence of facets/timeExtents causes intermediate
	//  results to "jerk".
	const isTimelineFiltered =
		!!(activeResult?.resultId && rootResult?.resultId) && activeResult.resultId !== rootResult.resultId;

	// fetch the root bars.
	const { data: rootData, error: rootError } = useAsyncCallback<HistogramEndpointResponse | null>(async () => {
		if (rootResult && timeProperty) {
			// prevent timeline and explorer from being out of sync when the dil definition changes
			if (rootResult?.sources[0].systemDefinitionId !== timeProperty.systemDefinitionId) {
				return;
			}
			// Remove the focused bars so they don't animate poorly.
			// setActiveData(null);
			if (rootResult.count > 0) {
				return await buildHistogramTimeline(rootResult, [timeProperty], {
					MaxBinResolution: maxBinResolution,
					...getBinCountsFromSize(dimensions.width, maxBinResolution),
				});
			} else {
				return null;
			}
		}
	}, [rootResult, timeProperty, maxBinResolution, dimensions.width]);

	// Fetch the filtered bars
	const { data: activeData, error: activeError } = useAsyncCallback(async () => {
		if (activeResult && rootData && timeProperty) {
			if (isTimelineFiltered && activeResult.count > 0) {
				const responseData = await buildHistogramTimeline(activeResult, [timeProperty], {
					StartTime: rootData.startTime,
					EndTime: rootData.endTime,
					MaxBinResolution: maxBinResolution,
					...getBinCountsFromSize(dimensions.width, maxBinResolution),
				});
				return responseData;
			} else {
				return null;
			}
		}
	}, [rootData, activeResult, timeProperty, isTimelineFiltered, maxBinResolution, dimensions.width]);

	// trigger error boundary
	const [, setError] = useState();
	useEffect(() => {
		if (rootError || activeError) {
			// @ts-ignore
			setError(() => {
				throw new Error('Timeline could not fetch data.');
			});
		}
	}, [activeError, rootError]);

	// Format second result to a form data the timeline library can use
	const formattedActiveData = useMemo(() => formatSRSTimeline(activeData), [activeData]);

	return (
		<ResizeSensor onResize={handleTimelineResize}>
			<div style={{ height: '100%', width: '100%', overflow: 'hidden' }} {...props}>
				{/* Loaders */}
				{!activeResult ? <LoadingIcon /> : activeResult.count === 0 ? <NonIdealState>No Data</NonIdealState> : null}

				{/* Chart */}
				{rootData && rootResult ? (
					rootData?.bins ? (
						<React.Fragment>
							{toolTip}

							<TimelineChart srsHistogramData={rootData} dimensions={dimensions} cropEmptyExtents>
								<Bars
									activeData={formattedActiveData}
									css={[styles.rootBar, !isTimelineFiltered ? styles.activeBar : null]}
								/>

								<Keyframe keyframe={timeExtents} updateKeyframe={addTimeFilter} />

								{/* Render a tooltip hitbox. */}
								<TimelineUseContext>
									{({ xScale, dataHeight, margin, yScale, data }) => {
										return (
											<g transform={`translate(${margin.left}, ${margin.top})`}>
												{data.map((d, i) => {
													// If check here to prevent adding tooltips for empty extents.
													// This should probably be refactored out to check if we doing empty extents in TimelineChart above
													if (sum(Object.values(d.values)) > 0) {
														const selectedBinValue =
															formattedActiveData &&
															formattedActiveData[i] &&
															formattedActiveData &&
															formattedActiveData[i].values.Count;
														const xCord = xScale(d.start);
														const MIN_BAR_HEIGHT = 8;
														let yCord = yScale(d.values.Count);
														let height = dataHeight - yCord;

														// If the bar is too small make it bigger
														if (MIN_BAR_HEIGHT > height) {
															yCord = dataHeight - MIN_BAR_HEIGHT;
															height = MIN_BAR_HEIGHT;
														}

														// Shrink the width by logarithmically to give a reasonable bar width and gap
														let width = xScale(d.end) - xScale(d.start);
														width =
															width *
															scaleLog()
																.domain([5 / 0.75, 100])
																.range([0.75, 0.99])
																.clamp(true)(width);

														// TODO: make this a dynamic format based on: const duration = d.end - d.start
														const dateFormatter = 'MM/DD/YYYY';

														// Creating a unique cypress test tag that effectively should append <month>-<year> to the end
														const cypressDataTag = `rect-bar-data-${d?.label?.split(' ')[0]?.toLowerCase()}-${d?.end?.getFullYear()}`;

														return (
															<rect
																cy-test={cypressDataTag}
																className={'my-rect'}
																css={css`
																	stroke: transparent;
																	fill: transparent;
																`}
																key={i}
																x={xCord}
																y={yCord}
																height={height ? height : 0}
																width={width}
																{...setupTrigger(
																	<ChartTooltip
																		title={d.label}
																		dateFormatter={dateFormatter}
																		content={{
																			Start: d.start,
																			End: d.end,
																			Duration: formatDistance(d.start, d.end),
																			'Selected Count': selectedBinValue == null ? null : selectedBinValue,
																			'Total Count': d.values.Count,
																		}}
																	/>
																)}
															/>
														);
													} else return null;
												})}
											</g>
										);
									}}
								</TimelineUseContext>
							</TimelineChart>
						</React.Fragment>
					) : (
						'No rootData. Reset to view everything.'
					)
				) : null}
			</div>
		</ResizeSensor>
	);
};

function getBinCountsFromSize(width: number, binSizing: TimelineBinResolution) {
	if (binSizing === TimelineBinResolution.Automatic) {
		return {
			MaxMajorBinCount: Math.max(Math.round(width / 120), 1), // 120 pixel/bar
			MaxMinorBinCount: Math.max(Math.round(width / 10), 1),
		};
	} else {
		return {};
	}
}

const styles = {
	rootBar: css`
		fill: ${Colors.LIGHT_GRAY2};
		stroke: ${Colors.WHITE};
	`,
	activeBar: css`
		fill: ${ColorAliases.PT_INTENT_PRIMARY};
	`,
};
